import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import LoadingOverlay from 'react-loading-overlay';
import GreenChoiceButton from './components/GreenChoiceButton';
import QuestionCard from './components/QuestionCard';
import QuestionText from './components/QuestionText';
import ConfirmButton from './components/ConfirmButton';
import AssessmentSection from './components/AssessmentSection';
import assessmentImage from './images';
import { commonConstant } from '../../common';
import { fetchAssessmentQuestions, submitAssessmentAnswers } from './assessment.service';
import './AssessmentQuestion.scss';

const CHOICE_LABELS = ['ก', 'ข', 'ค', 'ง', 'จ', 'ฉ', 'ช', 'ญ'];

// Map group key to the appropriate section image and choice button component
const GROUP_CONFIG = {
  nutrition: {
    sectionImage: assessmentImage.nutritionSection,
    sectionName: 'คำถามคุณภาพโภชนาการ',
    ChoiceButton: GreenChoiceButton,
  },
  water: {
    sectionImage: assessmentImage.waterSection,
    sectionName: 'คำถามการดื่มน้ำ',
    ChoiceButton: GreenChoiceButton,
  },
  sleep: {
    sectionImage: assessmentImage.sleepSection,
    sectionName: 'คำถามการนอนหลับ',
    ChoiceButton: GreenChoiceButton,
  },
  exerciseBehavior: {
    sectionImage: assessmentImage.exerciseBehaviorSection,
    sectionName: 'คำถามพฤติกรรมการออกกำลังกาย',
    ChoiceButton: GreenChoiceButton,
  },
  exerciseConfidence: {
    sectionImage: assessmentImage.exerciseConfidenceSection,
    sectionName: 'คำถามความมั่นใจในการออกกำลังกาย',
    ChoiceButton: GreenChoiceButton,
  },
};

// Default section order per landing type
const SECTION_KEYS = {
  nutrition: ['nutrition', 'water', 'sleep'],
  exercise: ['exerciseBehavior', 'exerciseConfidence'],
};

const getGroupConfig = (groupIndex, sectionKey) => {
  // Use sectionKey directly if it exists in GROUP_CONFIG
  if (GROUP_CONFIG[sectionKey]) {
    return GROUP_CONFIG[sectionKey];
  }
  // Fallback to index-based lookup
  const isExercise = ['exerciseBehavior', 'exerciseConfidence'].includes(sectionKey);
  const keys = isExercise ? SECTION_KEYS.exercise : SECTION_KEYS.nutrition;
  const key = keys[groupIndex] || keys[0];
  return GROUP_CONFIG[key] || GROUP_CONFIG.nutrition;
};

const AssessmentQuestion = () => {
  const history = useHistory();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const assessmentId = params.get('assessment_id') || '';
  const returnPath = params.get('return') || commonConstant.pathNutritionAssessmentLanding;
  const sectionKey = params.get('section') || 'nutrition';

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    const wrapper = document.getElementById('wrapper');
    if (wrapper) wrapper.style.maxWidth = '100%';
    return () => {
      if (wrapper) wrapper.style.maxWidth = '';
    };
  }, []);

  useEffect(() => {
    if (assessmentId) {
      setLoading(true);
      fetchAssessmentQuestions(assessmentId)
        .then((data) => {
          const sorted = (data?.hw_assessments_questions || []).sort(
            (a, b) => (a.display_order || 0) - (b.display_order || 0)
          );
          setQuestions(sorted);
          setGroups(data?.metaData?.groups || []);
        })
        .catch((err) => console.error('Fetch questions error:', err))
        .finally(() => setLoading(false));
    }
  }, [assessmentId]);

  const handleSelect = (questionId, choiceId, score) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { questionId, answerId: choiceId, score },
    }));
  };

  const handleSubmit = async () => {
    if (submitting) return;

    try {
      setSubmitting(true);
      const answersArray = Object.values(answers);
      const result = await submitAssessmentAnswers({
        assessmentId,
        answers: answersArray,
      });

      if (result?.success) {
        // Pop questions so stack is Home → Landing (one LIFF back → home).
        // Fallback to replace when there is no prior history entry.
        if (history.length > 1) {
          history.goBack();
        } else {
          history.replace(returnPath);
        }
      }
    } catch (err) {
      console.error('Submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Build grouped question sections
  const buildSections = () => {
    if (!groups || groups.length === 0) {
      // No groups defined, render all questions in one section
      return [{ groupIndex: 0, group: null, questions }];
    }

    // Try to match questions to groups via question_ids
    const sections = groups.map((group, index) => {
      const questionIds = group?.question_ids || [];
      const groupQuestions = questions.filter(
        (q) => questionIds.includes(q._id)
      );
      return { groupIndex: index, group, questions: groupQuestions };
    });

    const matchedSections = sections.filter((s) => s.questions.length > 0);

    // If no questions matched any group, show all in one section
    if (matchedSections.length === 0) {
      return [{ groupIndex: 0, group: null, questions }];
    }

    return matchedSections;
  };

  const allAnswered = questions.length > 0 && Object.keys(answers).length === questions.length;
  const sections = buildSections();

  if (loading) {
    return (
      <div className="assessment-question-component">
        <LoadingOverlay
          text="Loading"
          spinner
          active={true}
          styles={{ wrapper: { width: '100%', height: '100vh', position: 'fixed', top: 0, left: 0, zIndex: 10 } }}
        >
          <div />
        </LoadingOverlay>
      </div>
    );
  }

  if (!questions.length) {
    return (
      <div className="assessment-question-component">
        <p style={{ textAlign: 'center', padding: '2rem' }}>ไม่พบคำถาม</p>
      </div>
    );
  }

  return (
    <div className="assessment-question-component">
      {submitting && (
        <LoadingOverlay
          text="กำลังส่งคำตอบ..."
          spinner
          active={true}
          styles={{ wrapper: { width: '100%', height: '100vh', position: 'fixed', top: 0, left: 0, zIndex: 10 } }}
        >
          <div />
        </LoadingOverlay>
      )}

      {/* start bg detail */}
      <div className="bg-detail-container">
        <img src={assessmentImage.landingBgDetail} alt="" className="bg-detail" />
      </div>
      {/* end bg detail */}

      {/* start content container */}
      <div className="content-container">
        <div className="question-container">
          {sections.map((section) => {
            const config = getGroupConfig(section.groupIndex, sectionKey);
            const ChoiceButtonComponent = config.ChoiceButton;

            return (
              <React.Fragment key={section.groupIndex}>
                <AssessmentSection
                  image={config.sectionImage}
                  text={section.group?.group_name || config.sectionName}
                  count={section.questions.length}
                />

                {section.questions.map((question, qIndex) => {
                  const choices = question.question_value || [];
                  const selectedAnswerId = answers[question._id]?.answerId;

                  return (
                    <QuestionCard key={question._id || qIndex}>
                      <QuestionText>
                        {question.question || `${question.display_order || qIndex + 1}. คำถาม`}
                      </QuestionText>
                      {choices.map((choice, cIndex) => (
                        <ChoiceButtonComponent
                          key={choice._id || cIndex}
                          active={selectedAnswerId === choice._id}
                          choiceLabel={choice.label || CHOICE_LABELS[cIndex] || String(cIndex + 1)}
                          choiceText={choice.answer || ''}
                          onClick={() => handleSelect(question._id, choice._id, choice.score || 0)}
                        />
                      ))}
                    </QuestionCard>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>

        <div className="footer-container">
          <ConfirmButton
            text="ยืนยันคำตอบ"
            width="100%"
            disabled={!allAnswered || submitting}
            onClick={handleSubmit}
          />
        </div>
      </div>
      {/* end content container */}
    </div>
  );
};

export default AssessmentQuestion;
